import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Clock, Package, Truck, AlertTriangle, CheckCircle2, RefreshCw, RotateCcw } from "lucide-react";

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("ayrshire_token");
  return token
    ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
    : { "Content-Type": "application/json" };
}

type LiveOrder = {
  id: number;
  storeId: number;
  storeName: string | null;
  status: string;
  statusChangedAt: string | null;
  createdAt: string;
  totalPence: number;
  paymentMethod: string;
  refundedPence: number | null;
  stripePaymentIntentId: string | null;
  fulfilmentType: string;
  driverMode: string | null;
  customerName: string | null;
  deliveryAddress: string | null;
  notes: string | null;
  assignmentId: number | null;
  driverName: string | null;
  claimedAt: string | null;
  pickedUpAt: string | null;
};

const STATUS_LABEL: Record<string, string> = {
  pending: "Pending",
  confirmed: "Confirmed",
  preparing: "Preparing",
  ready: "Ready",
  out_for_delivery: "Out for Delivery",
};

const STATUS_COLOR: Record<string, string> = {
  pending: "bg-slate-100 text-slate-700",
  confirmed: "bg-blue-100 text-blue-700",
  preparing: "bg-amber-100 text-amber-700",
  ready: "bg-orange-100 text-orange-700",
  out_for_delivery: "bg-green-100 text-green-700",
};

function elapsed(isoString: string | null | undefined): { mins: number; label: string } {
  if (!isoString) return { mins: 0, label: "–" };
  const mins = Math.floor((Date.now() - new Date(isoString).getTime()) / 60000);
  if (mins < 1) return { mins: 0, label: "< 1 min" };
  if (mins < 60) return { mins, label: `${mins} min${mins === 1 ? "" : "s"}` };
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return { mins, label: `${h}h ${m}m` };
}

function TimerBadge({ isoString, label }: { isoString: string | null | undefined; label: string }) {
  const { mins, label: elapsedLabel } = elapsed(isoString);
  const color =
    mins >= 20 ? "bg-red-100 text-red-700 border-red-200" :
    mins >= 10 ? "bg-amber-100 text-amber-700 border-amber-200" :
    "bg-emerald-50 text-emerald-700 border-emerald-200";
  return (
    <div className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${color}`}>
      <Clock size={11} />
      <span>{label}: {elapsedLabel}</span>
    </div>
  );
}

function pence(p: number) {
  return `£${(p / 100).toFixed(2)}`;
}

type RefundModal = {
  paymentInfoLoaded: boolean;
  requestId: string;
  order: LiveOrder;
  amountInput: string;
  mode: "full" | "partial";
  busy: boolean;
  error: string | null;
  success: string | null;
};

export default function AdminLiveOrders() {
  const [orders, setOrders] = useState<LiveOrder[]>([]);
  const [runs, setRuns] = useState<Array<{ id: number; status: string; storeName: string | null; driverName: string | null; dropCount: number; deliveredCount: number; dropFeesTotalPence: number; fuelSupplementPence: number; hubFeePence: number; runNote: string | null; createdAt: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [, setTick] = useState(0);
  const [refundModal, setRefundModal] = useState<RefundModal | null>(null);

  async function fetchOrders() {
    try {
      const r = await fetch("/api/admin/live-orders", { headers: authHeaders() });
      if (r.ok) {
        const data = await r.json() as { items: LiveOrder[]; runs?: typeof runs };
        setOrders(data.items);
        setRuns(data.runs ?? []);
      }
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  }

  useEffect(() => {
    void fetchOrders();
    const poll = setInterval(() => void fetchOrders(), 15000);
    return () => clearInterval(poll);
  }, []);

  useEffect(() => {
    const tick = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(tick);
  }, []);

  const openRefund = async (order: LiveOrder) => {
    const requestId = crypto.randomUUID();
    setRefundModal({requestId,order,amountInput:'',mode:'full',busy:true,paymentInfoLoaded:false,error:null,success:null});
    try {
      const response = await fetch(`/api/admin/orders/${order.id}/refund-info`,{headers:authHeaders()});
      const info = await response.json() as {chargedPence:number;alreadyRefunded:number;maxRefundable:number;error?:string};
      if(!response.ok)throw Error(info.error ?? 'Unable to check the Stripe payment.');
      if(!Number.isSafeInteger(info.chargedPence)||!Number.isSafeInteger(info.alreadyRefunded)||!Number.isSafeInteger(info.maxRefundable)||info.maxRefundable<0)throw Error('Invalid Stripe payment information.');
      setRefundModal(current=>current?.requestId===requestId?{...current,order:{...order,totalPence:info.chargedPence,refundedPence:info.alreadyRefunded},amountInput:(info.maxRefundable/100).toFixed(2),busy:false,paymentInfoLoaded:true}:current);
    } catch(error) {
      setRefundModal(current=>current?.requestId===requestId?{...current,busy:false,error:error instanceof Error?error.message:'Unable to check the Stripe payment.'}:current);
    }
  };

  const submitRefund = async () => {
    if (!refundModal || !refundModal.paymentInfoLoaded || refundModal.busy) return;
    const alreadyRefunded = refundModal.order.refundedPence ?? 0;
    const maxPence = refundModal.order.totalPence - alreadyRefunded;
    const input = refundModal.amountInput.trim();
    const parts = /^(\d+)(?:\.(\d{1,2}))?$/.exec(input);
    const amountPence = parts ? Number(parts[1]) * 100 + Number((parts[2] ?? '').padEnd(2,'0')) : NaN;
    if (!Number.isSafeInteger(amountPence) || amountPence <= 0 || amountPence > maxPence) {
      setRefundModal((m) => m && { ...m, error: `Enter an amount between £0.01 and £${(maxPence / 100).toFixed(2)}` });
      return;
    }
    setRefundModal((m) => m && { ...m, busy: true, error: null });
    try {
      const res = await fetch(`/api/admin/orders/${refundModal.order.id}/refund`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ amountPence, idempotencyKey: refundModal.requestId + ':' + amountPence }),
      });
      const data = await res.json() as { error?: string; refundedPence?: number; totalRefundedPence?: number };
      if (!res.ok) throw new Error(data.error ?? "Refund failed");
      if (res.status === 202) {
        setRefundModal(m => m && {...m,busy:false,success:'Refund submitted to Stripe; awaiting confirmation.'});
        return;
      }
      setOrders((prev) =>
        prev.map((o) => o.id === refundModal.order.id ? { ...o, refundedPence: data.totalRefundedPence ?? o.refundedPence } : o)
      );
      setRefundModal((m) => m && { ...m, busy: false, success: `Refunded £${(amountPence / 100).toFixed(2)} successfully` });
    } catch (err) {
      setRefundModal((m) => m && { ...m, busy: false, error: (err as Error).message });
    }
  };

  const byStatus = {
    pending: orders.filter(o => o.status === "pending"),
    confirmed: orders.filter(o => o.status === "confirmed"),
    preparing: orders.filter(o => o.status === "preparing"),
    ready: orders.filter(o => o.status === "ready"),
    out_for_delivery: orders.filter(o => o.status === "out_for_delivery"),
  };

  return (
    <AdminLayout current="/admin/live-orders">
      <div className="space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Live Orders</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {orders.length} active order{orders.length !== 1 ? "s" : ""} across all stores · auto-refreshes every 15s
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-400">
              Last: {lastRefresh.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
            <button
              onClick={() => void fetchOrders()}
              className="flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-foreground bg-white border border-slate-200 rounded-lg px-3 py-1.5 transition-colors"
            >
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
        </div>

        {/* Summary stat row */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {(["pending", "confirmed", "preparing", "ready", "out_for_delivery"] as const).map(s => (
            <div key={s} className="bg-white rounded-xl border border-slate-100 p-3 text-center">
              <p className="text-2xl font-bold text-foreground">{byStatus[s].length}</p>
              <p className="text-xs text-slate-500 mt-0.5">{STATUS_LABEL[s]}</p>
            </div>
          ))}
        </div>

        {runs.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Multidrop runs ({runs.length})</p>
            <div className="space-y-2">
              {runs.map((run) => (
                <div key={`run-${run.id}`} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#1e3a5f] flex items-center justify-center text-white text-sm font-bold shrink-0">{run.dropCount}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 truncate">Run #{run.id} · {run.storeName ?? "Shop"}</p>
                    <p className="text-xs text-slate-500">{run.deliveredCount}/{run.dropCount} delivered · {run.driverName ? `Driver: ${run.driverName}` : "Awaiting a driver"}{run.runNote ? ` · “${run.runNote}”` : ""}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${run.status === "dispatching" ? "bg-amber-50 text-amber-700 border-amber-200" : run.status === "completed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>{run.status}</span>
                    <p className="text-xs text-slate-400 mt-1">Driver £{((run.dropFeesTotalPence + run.fuelSupplementPence) / 100).toFixed(2)} · Hub £{(run.hubFeePence / 100).toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          </div>
        ) : orders.length === 0 && runs.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-100 p-16 flex flex-col items-center gap-3 text-slate-400">
            <CheckCircle2 size={40} className="opacity-30" />
            <p className="font-medium">No active orders right now</p>
            <p className="text-sm">All caught up!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map(order => {
              const inCurrentStatus = elapsed(order.statusChangedAt ?? order.createdAt);
              const sinceOrdered = elapsed(order.createdAt);
              const isHubDelivery = order.driverMode === "hub" && order.fulfilmentType === "delivery";
              const waitingForDriver = isHubDelivery && order.status === "ready" && !order.assignmentId;
              const urgentWait = waitingForDriver && inCurrentStatus.mins >= 5;

              return (
                <div
                  key={order.id}
                  className={`bg-white rounded-2xl border p-4 space-y-3 ${urgentWait ? "border-red-300 shadow-sm shadow-red-100" : "border-slate-100"}`}
                >
                  {/* Top row */}
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-foreground text-base">#{order.id}</span>
                      <span className="font-semibold text-slate-700 text-sm">{order.storeName ?? "Unknown store"}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_COLOR[order.status] ?? "bg-slate-100 text-slate-600"}`}>
                        {STATUS_LABEL[order.status] ?? order.status}
                      </span>
                      {order.fulfilmentType === "delivery" ? (
                        <span className="text-xs text-slate-400 flex items-center gap-1"><Truck size={10} /> Delivery</span>
                      ) : (
                        <span className="text-xs text-slate-400 flex items-center gap-1"><Package size={10} /> Collection</span>
                      )}
                    </div>
                    <span className="font-bold text-slate-800">{pence(order.totalPence)}</span>
                  </div>

                  {/* Timer row */}
                  <div className="flex flex-wrap gap-2">
                    <TimerBadge isoString={order.createdAt} label="Order age" />
                    {order.statusChangedAt && order.statusChangedAt !== order.createdAt && (
                      <TimerBadge isoString={order.statusChangedAt} label={`In ${STATUS_LABEL[order.status] ?? order.status}`} />
                    )}
                    {order.claimedAt && (
                      <TimerBadge isoString={order.claimedAt} label="Driver assigned" />
                    )}
                    {order.pickedUpAt && (
                      <TimerBadge isoString={order.pickedUpAt} label="Picked up" />
                    )}
                  </div>

                  {/* Alerts */}
                  {urgentWait && (
                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                      <AlertTriangle size={14} className="text-red-600 flex-shrink-0" />
                      <p className="text-sm font-medium text-red-700">
                        Waiting for driver — {inCurrentStatus.label} in Ready state
                      </p>
                    </div>
                  )}
                  {waitingForDriver && !urgentWait && (
                    <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl px-3 py-2">
                      <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
                      <p className="text-sm font-medium text-amber-700">Waiting for a driver to claim</p>
                    </div>
                  )}

                  {/* Order / delivery note — the customer's instructions. On an
                      in-flight order this is the one an admin most needs to see
                      ("leave at door", "Meraki bar"); it was in the payload but
                      never shown. */}
                  {order.notes && order.notes.trim() && (
                    <div className="flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900">
                      <span className="shrink-0">📝</span>
                      <span className="min-w-0"><span className="font-semibold">Note: </span>{order.notes}</span>
                    </div>
                  )}

                  {/* Footer */}
                  <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 pt-1 border-t border-slate-50">
                    {order.customerName && <span>Customer: <strong className="text-slate-700">{order.customerName}</strong></span>}
                    {order.deliveryAddress && <span className="truncate max-w-xs">📍 {order.deliveryAddress}</span>}
                    {order.driverName && <span>Driver: <strong className="text-slate-700">{order.driverName}</strong></span>}
                    <span>Placed: {new Date(order.createdAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span>
                    <span className={`px-2 py-0.5 rounded-full font-semibold ${order.paymentMethod === "card" ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
                      {order.paymentMethod === "card" ? "Card" : "Cash"}
                    </span>
                    {(order.refundedPence ?? 0) > 0 && (
                      <span className="px-2 py-0.5 rounded-full font-semibold bg-amber-50 dark:bg-amber-900/20 text-amber-700">
                        Refunded {pence(order.refundedPence!)}
                      </span>
                    )}
                    {order.paymentMethod === "card" && order.stripePaymentIntentId && (order.refundedPence ?? 0) < order.totalPence && (
                      <button
                        onClick={() => openRefund(order)}
                        className="ml-auto flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700 border border-indigo-200 hover:bg-indigo-50 px-3 py-1.5 rounded-lg transition-colors"
                      >
                        <RotateCcw size={11} /> Refund
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Refund modal */}
      {refundModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => !refundModal.busy && setRefundModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-foreground">Refund Order #{refundModal.order.id}</h2>
              {!refundModal.busy && (
                <button onClick={() => setRefundModal(null)} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
              )}
            </div>

            <div className="bg-slate-50 rounded-xl p-4 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Store</span>
                <span className="font-medium">{refundModal.order.storeName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Charged by Stripe</span>
                <span className="font-medium">{refundModal.paymentInfoLoaded ? pence(refundModal.order.totalPence) : "Checking…"}</span>
              </div>
              {(refundModal.order.refundedPence ?? 0) > 0 && (
                <div className="flex justify-between text-amber-600">
                  <span>Already refunded</span>
                  <span className="font-medium">–{pence(refundModal.order.refundedPence!)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold border-t border-slate-200 pt-1 mt-1">
                <span>Refundable</span>
                <span>{refundModal.paymentInfoLoaded ? pence(refundModal.order.totalPence - (refundModal.order.refundedPence ?? 0)) : "Checking…"}</span>
              </div>
            </div>

            {refundModal.success ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-700 font-medium">{refundModal.success}</div>
            ) : (
              <>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const max = (refundModal.order.totalPence - (refundModal.order.refundedPence ?? 0)) / 100;
                      setRefundModal((m) => m && { ...m, mode: "full", amountInput: max.toFixed(2) });
                    }}
                    className={`flex-1 py-2 rounded-lg text-sm font-semibold border transition-colors ${refundModal.mode === "full" ? "bg-indigo-600 text-white border-indigo-600" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                  >
                    Full Refund
                  </button>
                  <button
                    onClick={() => setRefundModal((m) => m && { ...m, mode: "partial", amountInput: "" })}
                    className={`flex-1 py-2 rounded-lg text-sm font-semibold border transition-colors ${refundModal.mode === "partial" ? "bg-indigo-600 text-white border-indigo-600" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                  >
                    Partial Refund
                  </button>
                </div>

                {refundModal.mode === "partial" && (
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Amount to refund (£)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={((refundModal.order.totalPence - (refundModal.order.refundedPence ?? 0)) / 100).toFixed(2)}
                      value={refundModal.amountInput}
                      onChange={(e) => setRefundModal((m) => m && { ...m, amountInput: e.target.value })}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                      placeholder="0.00"
                    />
                  </div>
                )}

                {refundModal.error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{refundModal.error}</div>
                )}

                <button
                  onClick={() => void submitRefund()}
                  disabled={refundModal.busy || !refundModal.paymentInfoLoaded}
                  className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold rounded-xl transition-colors"
                >
                  {refundModal.busy ? "Processing…" : `Refund ${refundModal.mode === "full" ? pence(refundModal.order.totalPence - (refundModal.order.refundedPence ?? 0)) : refundModal.amountInput ? `£${parseFloat(refundModal.amountInput).toFixed(2)}` : "…"} via Stripe`}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
